import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, getDownloadToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import PriorityBadge from "@/components/PriorityBadge";
import { UpgradeLock } from "@/components/UpgradeLock";
import { UpgradeBanner } from "@/components/UpgradeBanner";
import { planAllows, normalizePlan } from "@/lib/plan";
import { temperature } from "@/lib/score";
import {
  Users, Flame, CalendarDays, TrendingUp, Download, Send, Search, Trash2,
  Loader2, Eye, Filter, Building2, Plug, Gauge, UserPlus, Copy, Cloud, Archive, HardDrive,
  Snowflake, Thermometer, ArrowDownWideNarrow, Trophy, FileText,
} from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === "MANAGER" || !!user?.isPlatformAdmin;
  const canCrm = planAllows(user, "crm_sync");
  const canArchive = planAllows(user, "archiving");
  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [company, setCompany] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalLeads: 0, highPriority: 0, activeReps: 0, events: 0 });
  const [board, setBoard] = useState({ rows: [], meId: null });
  const [q, setQ] = useState("");
  const [fEvent, setFEvent] = useState(ALL);
  const [fRep, setFRep] = useState(ALL);
  const [fPrio, setFPrio] = useState(ALL);
  const [fCat, setFCat] = useState(ALL);
  const [fTemp, setFTemp] = useState(ALL);
  const [sortScore, setSortScore] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [crmLead, setCrmLead] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem("klt_webhook") || "");
  const [sending, setSending] = useState(false);
  const [hubspotConfigured, setHubspotConfigured] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [hsConnecting, setHsConnecting] = useState(false);
  const [sfConfigured, setSfConfigured] = useState(false);
  const [sfConnecting, setSfConnecting] = useState(false);
  const [sfPushingId, setSfPushingId] = useState(null);
  const [pdConnected, setPdConnected] = useState(false);
  const [pdAvailable, setPdAvailable] = useState(false);
  const [pdConnecting, setPdConnecting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/leads"), api.get("/stats"), api.get("/integrations/hubspot/status"), api.get("/integrations/salesforce/status"), api.get("/integrations/pipedrive/status")])
      .then(([l, s, h, sf, pd]) => { setLeads(l.data); setStats(s.data); setHubspotConfigured(h.data.connected); setSfConfigured(sf.data.connected); setPdConnected(pd.data.connected); setPdAvailable(pd.data.configured); })
      .catch(() => toast.error("Échec du chargement des leads"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    const ev = fEvent !== ALL ? fEvent : "";
    const fetchBoard = () => api.get("/leaderboard", { params: { event: ev } }).then(({ data }) => setBoard(data)).catch(() => {});
    fetchBoard();
    const t = setInterval(fetchBoard, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fEvent]);

  const downloadEventReport = async () => {
    try {
      const token = await getDownloadToken();
      const ev = fEvent !== ALL ? `&event=${encodeURIComponent(fEvent)}` : "";
      window.open(`${API}/reports/event.pdf?auth=${token}${ev}`, "_blank");
      toast.success("Rapport de salon généré 📄");
    } catch {
      toast.error("Échec de la génération du rapport");
    }
  };

  const [storageInfo, setStorageInfo] = useState(null);
  const [archivingOld, setArchivingOld] = useState(false);

  const loadStorage = () => {
    if (user?.role !== "MANAGER") return;
    api.get("/storage/summary").then(({ data }) => setStorageInfo(data)).catch(() => {});
  };
  useEffect(loadStorage, [user]);

  const archiveOld = async () => {
    setArchivingOld(true);
    try {
      const { data } = await api.post("/leads/archive-old", { days: 90 });
      toast.success(`${data.archived} lead(s) archivé(s) vers le CRM · ${data.freedMb} Mo libérés`);
      load();
      loadStorage();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'archivage");
    } finally { setArchivingOld(false); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hs = params.get("hubspot");
    if (hs === "connected") { toast.success("HubSpot connecté 🟠"); setHubspotConfigured(true); window.history.replaceState({}, "", "/dashboard"); }
    else if (hs === "error") { toast.error("Échec HubSpot" + (params.get("reason") ? ` (${params.get("reason")})` : "")); window.history.replaceState({}, "", "/dashboard"); }
    const sf = params.get("salesforce");
    if (sf === "connected") { toast.success("Salesforce connecté ☁️"); setSfConfigured(true); window.history.replaceState({}, "", "/dashboard"); }
    else if (sf === "error") { toast.error("Échec Salesforce" + (params.get("reason") ? ` (${params.get("reason")})` : "")); window.history.replaceState({}, "", "/dashboard"); }
  }, []);

  useEffect(() => {
    if (isManager) {
      api.get("/team/members").then((r) => setMembers(r.data)).catch(() => {});
      api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    }
  }, [isManager]);

  const memberOptions = useMemo(
    () => members.filter((m) => m.role === "COMMERCIAL").map((m) => [m.id, m.name]),
    [members]
  );

  const events = useMemo(() => [...new Set(leads.map((l) => l.eventName).filter(Boolean))], [leads]);
  const reps = useMemo(() => {
    const m = {};
    leads.forEach((l) => { if (l.ownerId) m[l.ownerId] = l.ownerName || l.ownerId; });
    return Object.entries(m);
  }, [leads]);
  const cats = useMemo(() => [...new Set(leads.map((l) => l.aiClassification?.category).filter(Boolean))], [leads]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (fEvent !== ALL && l.eventName !== fEvent) return false;
      if (fRep !== ALL && l.ownerId !== fRep) return false;
      if (fPrio !== ALL && (l.aiClassification?.priorityLevel || "") !== fPrio) return false;
      if (fCat !== ALL && (l.aiClassification?.category || "") !== fCat) return false;
      if (fTemp !== ALL) {
        const t = temperature(l.aiClassification?.conversionScore);
        if (!t || t.key !== fTemp) return false;
      }
      if (q) {
        const c = l.contactInfo || {};
        const hay = `${c.firstName} ${c.lastName} ${c.company} ${c.role} ${c.email}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [leads, fEvent, fRep, fPrio, fCat, fTemp, q]);

  const displayed = useMemo(() => {
    if (!sortScore) return filtered;
    return [...filtered].sort((a, b) => {
      const sa = a.aiClassification?.conversionScore;
      const sb = b.aiClassification?.conversionScore;
      return (typeof sb === "number" ? sb : -1) - (typeof sa === "number" ? sa : -1);
    });
  }, [filtered, sortScore]);

  const exportCsv = () => {
    if (displayed.length === 0) return toast.error("Aucun lead à exporter");
    const headers = ["Prénom", "Nom", "Entreprise", "Fonction", "Email", "Téléphone", "Salon", "Commercial", "Priorité", "Score", "Catégorie", "Tags", "Méthode"];
    const rows = displayed.map((l) => {
      const c = l.contactInfo || {};
      const a = l.aiClassification || {};
      return [c.firstName, c.lastName, c.company, c.role, c.email, c.phone, l.eventName, l.ownerName, a.priorityLevel, a.conversionScore, a.category, (a.tags || []).join("|"), l.captureMethod];
    });
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kallitag-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${displayed.length} leads exportés en CSV`);
  };

  const openCrm = (lead) => { setCrmLead(lead); setCrmOpen(true); };
  const sendCrm = async () => {
    if (!webhookUrl.trim()) return toast.error("Saisissez une URL de webhook");
    setSending(true);
    localStorage.setItem("klt_webhook", webhookUrl.trim());
    try {
      const { data } = await api.post(`/leads/${crmLead.id}/send-crm`, { webhookUrl: webhookUrl.trim() });
      if (data.success) toast.success(`Lead envoyé au CRM (HTTP ${data.status})`);
      else toast.error(`Le webhook a répondu ${data.status}`);
      setCrmOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi au CRM");
    } finally {
      setSending(false);
    }
  };

  const remove = async (lead) => {
    await api.delete(`/leads/${lead.id}`);
    toast.success("Lead supprimé");
    load();
  };

  const pushHubspot = async (lead) => {
    if (!hubspotConfigured) return toast.error("HubSpot non connecté. Cliquez sur « Connecter mon HubSpot ».");
    setPushingId(lead.id);
    try {
      await api.post(`/leads/${lead.id}/hubspot`);
      toast.success("Contact synchronisé vers HubSpot 🟠");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de la synchronisation HubSpot");
    } finally {
      setPushingId(null);
    }
  };

  const connectHubspot = async () => {
    setHsConnecting(true);
    try {
      const { data } = await api.get("/hubspot/oauth/start");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "HubSpot OAuth indisponible");
      setHsConnecting(false);
    }
  };

  const disconnectHubspot = async () => {
    await api.post("/integrations/hubspot/disconnect");
    setHubspotConfigured(false);
    toast.success("HubSpot déconnecté");
  };

  const connectSalesforce = async () => {
    setSfConnecting(true);
    try {
      const { data } = await api.get("/salesforce/oauth/start");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salesforce OAuth indisponible");
      setSfConnecting(false);
    }
  };

  const disconnectSalesforce = async () => {
    await api.post("/integrations/salesforce/disconnect");
    setSfConfigured(false);
    toast.success("Salesforce déconnecté");
  };

  const connectPipedrive = async () => {
    setPdConnecting(true);
    try {
      const { data } = await api.get("/pipedrive/oauth/start");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pipedrive OAuth indisponible");
      setPdConnecting(false);
    }
  };

  const disconnectPipedrive = async () => {
    await api.post("/integrations/pipedrive/disconnect");
    setPdConnected(false);
    toast.success("Pipedrive déconnecté");
  };

  const pushSalesforce = async (lead) => {
    if (!sfConfigured) return toast.error("Salesforce non connecté. Cliquez sur « Connecter mon Salesforce ».");
    setSfPushingId(lead.id);
    try {
      await api.post(`/leads/${lead.id}/salesforce`);
      toast.success("Contact synchronisé vers Salesforce ☁️");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de la synchronisation Salesforce");
    } finally {
      setSfPushingId(null);
    }
  };

  const kpis = [
    { label: "Total Leads", value: stats.totalLeads, icon: Users, color: "text-blue-500 bg-blue-500/10" },
    { label: "Priorité Haute", value: stats.highPriority, icon: Flame, color: "text-rose-500 bg-rose-500/10" },
    { label: "Commerciaux actifs", value: stats.activeReps, icon: TrendingUp, color: "text-emerald-500 bg-emerald-500/10" },
    { label: "Salons", value: stats.events, icon: CalendarDays, color: "text-violet-500 bg-violet-500/10" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      {normalizePlan(user?.plan) && !canCrm && (
        <UpgradeBanner
          feature="crm_sync"
          title="Débloquez la synchronisation CRM avec le forfait Équipe"
          benefit="Envoyez vos prospects vers HubSpot & Salesforce en 1 clic et collaborez à plusieurs sur les mêmes leads."
        />
      )}
      {normalizePlan(user?.plan) && canCrm && !canArchive && (
        <UpgradeBanner
          feature="archiving"
          title="Passez à Entreprise pour l'archivage automatique et l'IA avancée"
          benefit="Archivage CRM auto pour alléger le stockage, comparateur différentiel IA et synthèse de compte."
        />
      )}
      {isManager && storageInfo && (storageInfo.overBudget || storageInfo.oldLeads > 0) && (
        <div className="mb-5 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid="storage-suggestion-banner">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
            <HardDrive className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="font-semibold text-amber-700 dark:text-amber-400">
              {storageInfo.overBudget ? "Stockage audio élevé" : "Alléger le stockage"} · {storageInfo.mb.toFixed(1)} Mo
            </p>
            <p className="text-sm text-foreground/80 mt-0.5">
              {storageInfo.oldLeads > 0
                ? `${storageInfo.oldLeads} prospect(s) de plus de 90 jours peuvent être archivés vers votre CRM (texte conservé, audio libéré).`
                : "Archivez vos anciens prospects vers votre CRM pour libérer de l'espace (texte conservé dans l'appli)."}
            </p>
          </div>
          {!canArchive ? (
            <UpgradeLock feature="archiving" label="Archivage auto" />
          ) : storageInfo.crmConnected ? (
            <Button onClick={archiveOld} disabled={archivingOld} data-testid="archive-old-button" className="rounded-xl">
              {archivingOld ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <Archive className="w-4 h-4 mr-2" />} Archiver les anciens leads
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">Connectez HubSpot ou Salesforce pour archiver.</span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{isManager ? "Gestion Équipe" : "Vue Commercial"}</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">{isManager ? "Dashboard de l'équipe" : "Mes Leads"}</h1>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto max-w-full pb-1 klt-hscroll">
          {!canCrm ? (
            <UpgradeLock feature="crm_sync" label="Sync CRM" />
          ) : hubspotConfigured ? (
            <Button onClick={disconnectHubspot} variant="outline" data-testid="hubspot-disconnect-button" className="rounded-xl shrink-0 whitespace-nowrap">
              <Plug className="w-4 h-4 mr-2 text-orange-500" /> HubSpot connecté
            </Button>
          ) : (
            <Button onClick={connectHubspot} disabled={hsConnecting} variant="outline" data-testid="hubspot-connect-button" className="rounded-xl shrink-0 whitespace-nowrap">
              {hsConnecting ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <Plug className="w-4 h-4 mr-2" />} Connecter mon HubSpot
            </Button>
          )}
          {canCrm && (sfConfigured ? (
            <Button onClick={disconnectSalesforce} variant="outline" data-testid="salesforce-disconnect-button" className="rounded-xl shrink-0 whitespace-nowrap">
              <Cloud className="w-4 h-4 mr-2 text-sky-500" /> Salesforce connecté
            </Button>
          ) : (
            <Button onClick={connectSalesforce} disabled={sfConnecting} variant="outline" data-testid="salesforce-connect-button" className="rounded-xl shrink-0 whitespace-nowrap">
              {sfConnecting ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <Cloud className="w-4 h-4 mr-2" />} Connecter mon Salesforce
            </Button>
          ))}
          {canCrm && pdAvailable && (pdConnected ? (
            <Button onClick={disconnectPipedrive} variant="outline" data-testid="pipedrive-disconnect-button" className="rounded-xl shrink-0 whitespace-nowrap">
              <Plug className="w-4 h-4 mr-2 text-emerald-500" /> Pipedrive connecté
            </Button>
          ) : (
            <Button onClick={connectPipedrive} disabled={pdConnecting} variant="outline" data-testid="pipedrive-connect-button" className="rounded-xl shrink-0 whitespace-nowrap">
              {pdConnecting ? <Loader2 className="w-4 h-4 mr-2 klt-spin" /> : <Plug className="w-4 h-4 mr-2 text-emerald-500" />} Connecter mon Pipedrive
            </Button>
          ))}
          {isManager && (
            <Button onClick={() => setInviteOpen(true)} data-testid="invite-commercial-button" className="rounded-xl shrink-0 whitespace-nowrap">
              <UserPlus className="w-4 h-4 mr-2" /> Ajouter un commercial
            </Button>
          )}
          <Button onClick={downloadEventReport} data-testid="event-report-button" variant="outline" className="rounded-xl shrink-0 whitespace-nowrap">
            <FileText className="w-4 h-4 mr-2" /> Rapport de salon
          </Button>
          <Button onClick={exportCsv} data-testid="export-csv-button" variant="outline" className="rounded-xl shrink-0 whitespace-nowrap">
            <Download className="w-4 h-4 mr-2" /> Exporter CSV / Excel
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={k.label} data-testid={`kpi-${i}`} className="klt-fade-up rounded-2xl border border-border bg-card p-5 shadow-sm" style={{ animationDelay: `${i * 60}ms` }}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.color}`}><Icon className="w-5 h-5" /></div>
              <div className="mt-3 font-heading text-3xl font-extrabold">{k.value}</div>
              <div className="text-sm text-muted-foreground">{k.label}</div>
            </div>
          );
        })}
      </div>

      {/* Team leaderboard (live) */}
      {board.rows.length > 0 && (
        <div className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-sm klt-fade-up" data-testid="leaderboard">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Classement de l'équipe{fEvent !== ALL ? ` · ${fEvent}` : ""}
            </h3>
            <span className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> En direct
            </span>
          </div>
          <div className="space-y-2">
            {board.rows.slice(0, 10).map((r, i) => (
              <div key={r.ownerId} data-testid={`leaderboard-row-${i}`} className={`flex items-center gap-3 rounded-xl border p-3 ${r.ownerId === board.meId ? "border-primary/50 bg-primary/5" : "border-border"}`}>
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-heading font-extrabold text-sm ${i === 0 ? "bg-amber-500/15 text-amber-600" : i === 1 ? "bg-slate-400/20 text-slate-500" : i === 2 ? "bg-orange-700/15 text-orange-700 dark:text-orange-400" : "bg-secondary text-muted-foreground"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.ownerName}{r.ownerId === board.meId && <span className="text-xs text-primary ml-1">(moi)</span>}</div>
                </div>
                {r.hot > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-600 font-semibold inline-flex items-center gap-1"><Flame className="w-3 h-3" />{r.hot}</span>}
                <span className="font-heading font-extrabold text-lg tabular-nums">{r.total}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Filter className="w-4 h-4" /> Filtres</div>
          <button onClick={() => setSortScore((s) => !s)} data-testid="sort-by-score-toggle" className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${sortScore ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
            <ArrowDownWideNarrow className="w-3.5 h-3.5" /> Trier par score
          </button>
        </div>
        <div className={`grid gap-3 sm:grid-cols-2 ${isManager ? "lg:grid-cols-6" : "lg:grid-cols-5"}`}>
          <div className="relative lg:col-span-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9" />
          </div>
          <FilterSelect testid="leads-filter-show" value={fEvent} onChange={setFEvent} placeholder="Salon" options={events.map((e) => [e, e])} />
          {isManager && (
            <FilterSelect testid="leads-filter-rep" value={fRep} onChange={setFRep} placeholder="Commercial" options={memberOptions.length ? memberOptions : reps} />
          )}
          <FilterSelect testid="leads-filter-priority" value={fPrio} onChange={setFPrio} placeholder="Priorité" options={[["HAUTE", "HAUTE"], ["MOYENNE", "MOYENNE"], ["BASSE", "BASSE"]]} />
          <FilterSelect testid="leads-filter-category" value={fCat} onChange={setFCat} placeholder="Catégorie" options={cats.map((c) => [c, c])} />
          <FilterSelect testid="leads-filter-temperature" value={fTemp} onChange={setFTemp} placeholder="Température" options={[["chaud", "Chaud"], ["tiede", "Tiède"], ["froid", "Froid"]]} />
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-border bg-card shadow-sm overflow-hidden" data-testid="leads-table">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="w-6 h-6 klt-spin" /></div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Building2 className="w-10 h-10 mx-auto opacity-40" />
            <p className="mt-3 font-medium">Aucun lead pour le moment</p>
            <Button onClick={() => navigate("/")} variant="outline" className="mt-4 rounded-xl">Capturer un prospect</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Contact</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Entreprise</th>
                  <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Salon</th>
                  <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Commercial</th>
                  <th className="text-left font-medium px-4 py-3">Priorité</th>
                  <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Score</th>
                  <th className="text-left font-medium px-4 py-3 hidden xl:table-cell">Tags</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((l) => {
                  const c = l.contactInfo || {};
                  const a = l.aiClassification || {};
                  return (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-border hover:bg-secondary/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-semibold">{c.firstName} {c.lastName || <span className="text-muted-foreground">—</span>}</div>
                        <div className="text-xs text-muted-foreground">{c.role || c.email}</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">{c.company || "—"}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{l.eventName || "—"}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{l.ownerName || "—"}</td>
                      <td className="px-4 py-3">{a.priorityLevel ? <PriorityBadge level={a.priorityLevel} /> : <span className="text-muted-foreground text-xs">—</span>}</td>
                      <td className="px-4 py-3 hidden sm:table-cell">{typeof a.conversionScore === "number" ? <div className="flex items-center gap-1.5"><ScorePill score={a.conversionScore} /><TempBadge score={a.conversionScore} /></div> : <span className="text-muted-foreground text-xs">—</span>}</td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(a.tags || []).slice(0, 2).map((t, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400">{t}</span>)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={() => navigate(`/lead/${l.id}`)} data-testid={`view-lead-${l.id}`}><Eye className="w-4 h-4" /></Button>
                          {canCrm && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg text-primary" onClick={() => openCrm(l)} data-testid={`send-crm-webhook-button`}><Send className="w-4 h-4" /></Button>}
                          {canCrm && <Button size="icon" variant="ghost" className={`h-8 w-8 rounded-lg ${l.hubspot ? "text-orange-500" : "text-orange-500/70"}`} onClick={() => pushHubspot(l)} disabled={pushingId === l.id} data-testid={`hubspot-push-button`} title="Envoyer vers HubSpot">{pushingId === l.id ? <Loader2 className="w-4 h-4 klt-spin" /> : <Plug className="w-4 h-4" />}</Button>}
                          {canCrm && <Button size="icon" variant="ghost" className={`h-8 w-8 rounded-lg ${l.salesforce ? "text-sky-500" : "text-sky-500/70"}`} onClick={() => pushSalesforce(l)} disabled={sfPushingId === l.id} data-testid={`salesforce-push-button`} title="Envoyer vers Salesforce">{sfPushingId === l.id ? <Loader2 className="w-4 h-4 klt-spin" /> : <Cloud className="w-4 h-4" />}</Button>}
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg text-rose-500" onClick={() => remove(l)} data-testid={`delete-lead-${l.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-3">{displayed.length} lead(s) affiché(s)</p>

      {/* Onboarding commercial dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ajouter un commercial</DialogTitle>
            <DialogDescription>Les accès sont gérés depuis kallitag.fr — aucun mot de passe à créer ici.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-secondary/50 p-4 text-sm space-y-2" data-testid="onboarding-steps">
              <p className="font-medium text-foreground">Pour ajouter un membre à votre équipe :</p>
              <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground">
                <li>Le commercial doit avoir un compte <span className="font-medium text-foreground">kallitag.fr</span> rattaché à <span className="font-medium text-foreground">{company?.name || "votre entreprise"}</span>.</li>
                <li>Assurez-vous que son abonnement <span className="font-medium text-foreground">Lead Capture</span> est actif.</li>
                <li>Il se connecte sur cette app avec son email : un code de connexion lui est envoyé.</li>
                <li>Il apparaîtra automatiquement dans votre équipe, ses leads regroupés avec les vôtres.</li>
              </ol>
            </div>
            {members.length > 0 && (
              <div className="text-sm">
                <p className="text-muted-foreground mb-2">Équipe actuelle ({members.length})</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="team-members-list">
                  {members.map((m) => (
                    <div key={m.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                      <div>
                        <div className="font-medium">{m.name}</div>
                        <div className="text-xs text-muted-foreground">{m.email}</div>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{m.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setInviteOpen(false)} className="rounded-xl" data-testid="close-onboarding-button">J'ai compris</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRM webhook dialog */}
      <Dialog open={crmOpen} onOpenChange={setCrmOpen}>
        <DialogContent data-testid="crm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Envoyer vers le CRM</DialogTitle>
            <DialogDescription>Le lead sera envoyé en POST JSON à l'URL du webhook de votre CRM.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>URL du Webhook</Label>
            <Input data-testid="webhook-url-input" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://votre-crm.com/api/webhook" />
          </div>
          {crmLead && (
            <div className="rounded-xl bg-secondary/50 p-3 text-sm">
              <span className="font-semibold">{crmLead.contactInfo?.firstName} {crmLead.contactInfo?.lastName}</span> · {crmLead.contactInfo?.company}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrmOpen(false)} className="rounded-xl">Annuler</Button>
            <Button onClick={sendCrm} disabled={sending} data-testid="confirm-send-crm-button" className="rounded-xl">
              {sending ? <Loader2 className="w-4 h-4 klt-spin" /> : <><Send className="w-4 h-4 mr-2" /> Envoyer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScorePill({ score }) {
  const color = score >= 66 ? "text-emerald-600 bg-emerald-500/10" : score >= 33 ? "text-amber-600 bg-amber-500/10" : "text-rose-600 bg-rose-500/10";
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}><Gauge className="w-3 h-3" />{score}%</span>;
}

function TempBadge({ score }) {
  const t = temperature(score);
  if (!t) return null;
  const Icon = t.key === "chaud" ? Flame : t.key === "tiede" ? Thermometer : Snowflake;
  return <span data-testid={`temp-badge-${t.key}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${t.cls}`}><Icon className="w-3 h-3" />{t.label}</span>;
}

function FilterSelect({ testid, value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder} — Tous</SelectItem>
        {options.map(([val, label]) => <SelectItem key={val} value={val}>{label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
